import { ReactNode } from 'react';
import { SxProps, Theme } from '@mui/material';
import { ReportColumnFormat } from '@/@types/reports';

const DEFAULT_STICKY_COLUMN_WIDTH = '8rem';

/* eslint-disable no-unused-vars */
export type ColumnDefinitionType<T> = {
  key: keyof T;
  header: string;
  sort?: string;
  render?: (data: T) => ReactNode;
  formatValue?: (value: unknown) => ReactNode;
  width?: string | number;
  numeric?: boolean;
  format?: ReportColumnFormat;
  disablePadding?: boolean;
  align?: 'left' | 'center' | 'right';
  sticky?: 'left' | 'right';
};

function getColumnWidth<T>(column: ColumnDefinitionType<T>) {
  if (typeof column.width === 'number') {
    return `${column.width}px`;
  }

  return column.width || DEFAULT_STICKY_COLUMN_WIDTH;
}

function getStickyOffset<T>(
  columns: ColumnDefinitionType<T>[],
  columnIndex: number,
  sticky: 'left' | 'right'
) {
  const columnsBeforeStickyColumn = columns
    .slice(0, columnIndex)
    .filter((column) => column.sticky === sticky);
  const columnsAfterStickyColumn = columns
    .slice(columnIndex + 1)
    .filter((column) => column.sticky === sticky);
  const offsetColumns =
    sticky === 'left' ? columnsBeforeStickyColumn : columnsAfterStickyColumn;

  if (!offsetColumns.length) {
    return 0;
  }

  return `calc(${offsetColumns.map(getColumnWidth).join(' + ')})`;
}

export function getColumnSx<T>(
  columns: ColumnDefinitionType<T>[],
  column: ColumnDefinitionType<T>,
  columnIndex: number,
  options: { isHeader?: boolean } = {}
): SxProps<Theme> {
  const width = column.width ? getColumnWidth(column) : undefined;

  if (!column.sticky) {
    return { width, minWidth: width };
  }

  return {
    position: 'sticky',
    [column.sticky]: getStickyOffset(columns, columnIndex, column.sticky),
    zIndex: options.isHeader ? 4 : 2,
    backgroundColor: options.isHeader ? 'grey.200' : 'background.paper',
    width: getColumnWidth(column),
    minWidth: getColumnWidth(column),
  };
}
